(() => {
  const SEARCH_DELAY = 250;
  const DEFAULT_SUGGESTION_QUERIES = [
    'cocktail pick',
    'food pick',
    'bamboo',
    'toothpick',
    'straw',
    'napkin',
    'paper',
    'party',
    'drink',
    'gold'
  ];

  const absoluteUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.delete('oseid');
      parsed.searchParams.delete('preview_theme_id');
      return parsed.href;
    } catch (_) { return url || ''; }
  };

  const debounce = (callback, delay) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  };

  class ProductPicker {
    constructor(root, options = {}) {
      if (!root || typeof root.querySelector !== 'function') {
        throw new Error('[ProductPicker] root element is required');
      }
      this.root = root;
      this.options = {
        limit: 10,
        messages: {},
        recommendationsProductId: null,
        getExcludedIds: () => [],
        onConfirm: null,
        ...options
      };
      this.dialog = root.querySelector('[data-product-picker-dialog]');
      this.backdrop = root.querySelector('[data-product-picker-backdrop]');
      this.searchInput = root.querySelector('[data-product-picker-search]');
      this.resultContainer = root.querySelector('[data-product-picker-results]');
      this.statusEl = root.querySelector('[data-product-picker-status]');
      this.closeButton = root.querySelector('[data-product-picker-close]');
      this.confirmButton = root.querySelector('[data-product-picker-confirm]');
      this.countEl = root.querySelector('[data-product-picker-count]');
      this.titleEl = root.querySelector('[data-product-picker-title]');
      this.subtitleEl = root.querySelector('[data-product-picker-subtitle]');
      if (!this.dialog) return;
      this.isOpen = false;
      this.draft = new Map();
      this.recommendationResults = [];
      this.recommendationsLoaded = false;
      this.suggestionResults = [];
      this.suggestionsLoaded = false;
      this.lastTrigger = null;
      this.boundBackdropClick = this.handleBackdropClick.bind(this);
      this.boundClose = this.handleClose.bind(this);
      this.boundConfirm = this.handleConfirm.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
      this.boundResultChange = this.handleResultChange.bind(this);
      this.boundSearchInput = debounce(this.runSearch.bind(this), SEARCH_DELAY);
      this.attach();
    }

    attach() {
      if (this.closeButton) this.closeButton.addEventListener('click', this.boundClose);
      if (this.confirmButton) this.confirmButton.addEventListener('click', this.boundConfirm);
      if (this.searchInput) this.searchInput.addEventListener('input', this.boundSearchInput);
      if (this.resultContainer) this.resultContainer.addEventListener('change', this.boundResultChange);
      if (this.backdrop) this.backdrop.addEventListener('click', this.boundBackdropClick);
    }

    destroy() {
      if (this.closeButton) this.closeButton.removeEventListener('click', this.boundClose);
      if (this.confirmButton) this.confirmButton.removeEventListener('click', this.boundConfirm);
      if (this.searchInput) this.searchInput.removeEventListener('input', this.boundSearchInput);
      if (this.resultContainer) this.resultContainer.removeEventListener('change', this.boundResultChange);
      if (this.backdrop) this.backdrop.removeEventListener('click', this.boundBackdropClick);
      document.removeEventListener('keydown', this.boundKeydown, true);
    }

    setMessages(messages) {
      this.options.messages = { ...this.options.messages, ...(messages || {}) };
      this.applyTranslations();
    }

    applyTranslations() {
      const m = this.options.messages;
      const set = (node, value) => { if (node && value) node.textContent = value; };
      set(this.titleEl, m.title);
      set(this.subtitleEl, m.subtitle);
      if (this.searchInput && m.placeholder) this.searchInput.placeholder = m.placeholder;
      const label = this.root.querySelector('[data-product-picker-search-label]');
      set(label, m.searchLabel);
      if (this.closeButton) this.closeButton.setAttribute('aria-label', m.close || 'Close');
      if (this.confirmButton) {
        const label = m.addSelected || 'Add selected products';
        this.confirmButton.textContent = label;
      }
    }

    setOptions(patch) {
      this.options = { ...this.options, ...patch };
    }

    open() {
      if (!this.dialog || this.isOpen) return;
      this.isOpen = true;
      this.draft = new Map();
      if (this.searchInput) this.searchInput.value = '';
      this.applyTranslations();
      this.updateCount();
      if (this.backdrop) {
        this.backdrop.hidden = false;
        // Force reflow so the transition kicks in:
        void this.backdrop.offsetWidth;
        this.backdrop.classList.add('is-open');
      }
      this.dialog.hidden = false;
      void this.dialog.offsetWidth;
      this.dialog.classList.add('is-open');
      this.dialog.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-picker-open');
      document.addEventListener('keydown', this.boundKeydown, true);
      window.requestAnimationFrame(() => {
        if (this.searchInput) this.searchInput.focus();
        this.loadRecommendations();
        this.root.dispatchEvent(new CustomEvent('product-picker:open', { bubbles: true }));
      });
    }

    close() {
      if (!this.dialog || !this.isOpen) return;
      this.isOpen = false;
      this.dialog.classList.remove('is-open');
      if (this.backdrop) this.backdrop.classList.remove('is-open');
      this.dialog.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-picker-open');
      document.removeEventListener('keydown', this.boundKeydown, true);
      const finalize = () => {
        this.dialog.hidden = true;
        if (this.backdrop) this.backdrop.hidden = true;
        this.draft = new Map();
        if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
          this.lastTrigger.focus();
        }
        this.root.dispatchEvent(new CustomEvent('product-picker:close', { bubbles: true }));
      };
      // Let the CSS transition play out before hiding:
      window.setTimeout(finalize, 200);
    }

    setLastTrigger(trigger) {
      this.lastTrigger = trigger;
    }

    handleClose() {
      this.close();
    }

    handleBackdropClick() {
      this.close();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    handleResultChange(event) {
      const checkbox = event.target.closest('input[type="checkbox"]');
      const row = event.target.closest('[data-picker-result-id]');
      if (!checkbox || !row) return;
      const product = row._quoteProduct;
      if (!product) return;
      if (checkbox.checked) {
        if (this.draft.size + 1 > this.options.limit) {
          checkbox.checked = false;
          this.setStatus(this.formatLimitMessage());
          return;
        }
        this.draft.set(product.id, product);
      } else {
        this.draft.delete(product.id);
      }
      row.classList.toggle('is-selected', checkbox.checked);
      this.updateCount();
    }

    handleConfirm() {
      const products = [...this.draft.values()];
      if (products.length === 0) {
        this.close();
        return;
      }
      const detail = { products };
      this.root.dispatchEvent(new CustomEvent('product-picker:confirm', { detail, bubbles: true }));
      if (typeof this.options.onConfirm === 'function') {
        try { this.options.onConfirm(products); } catch (error) { console.error(error); }
      }
      this.close();
    }

    setStatus(message) {
      if (this.statusEl) this.statusEl.textContent = message || '';
    }

    formatLimitMessage() {
      const template = this.options.messages.limit || 'You can select up to {{ limit }} products per quote.';
      return template.replace('{{ limit }}', String(this.options.limit));
    }

    updateCount() {
      if (!this.countEl) return;
      const count = this.draft.size;
      const template = count === 1
        ? (this.options.messages.selected_singular || '1 product selected')
        : (this.options.messages.selected_count || '{{ count }} selected').replace('{{ count }}', String(count));
      this.countEl.textContent = template;
      if (this.confirmButton) this.confirmButton.disabled = count === 0;
    }

    excludedIds() {
      try {
        return new Set((this.options.getExcludedIds() || []).map(String));
      } catch (_) {
        return new Set();
      }
    }

    async loadRecommendations() {
      if (!this.options.recommendationsProductId) {
        return this.loadSuggestedProducts();
      }
      if (this.recommendationsLoaded) {
        this.renderResults(this.recommendationResults, this.options.messages.recommended);
        return;
      }
      this.setStatus(this.options.messages.loading);
      try {
        const root = this.rootRoute();
        const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(this.options.recommendationsProductId)}&limit=8&intent=complementary`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('recommendations');
        const payload = await response.json();
        this.recommendationResults = Array.isArray(payload.products) ? payload.products : [];
        this.recommendationsLoaded = true;
        this.renderResults(this.recommendationResults, this.options.messages.recommended);
      } catch (_) {
        this.setStatus(this.options.messages.error);
        this.resultContainer.innerHTML = '';
      }
    }

    suggestionQueries() {
      const start = Math.floor(Math.random() * DEFAULT_SUGGESTION_QUERIES.length);
      return [
        ...DEFAULT_SUGGESTION_QUERIES.slice(start),
        ...DEFAULT_SUGGESTION_QUERIES.slice(0, start)
      ];
    }

    async fetchSearchProducts(term, limit = 10) {
      const root = this.rootRoute();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': String(limit),
        'resources[options][unavailable_products]': 'show'
      });
      const response = await fetch(`${root}search/suggest.json?${params}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('search');
      const payload = await response.json();
      const products = payload.resources && payload.resources.results && payload.resources.results.products;
      return Array.isArray(products) ? products : [];
    }

    async loadSuggestedProducts() {
      if (this.suggestionsLoaded) {
        this.renderResults(this.suggestionResults, this.options.messages.recommended);
        return;
      }
      this.setStatus(this.options.messages.loading);
      try {
        let products = [];
        for (const term of this.suggestionQueries().slice(0, 4)) {
          products = await this.fetchSearchProducts(term, 8);
          if (products.length > 0) break;
        }
        this.suggestionResults = products;
        this.suggestionsLoaded = true;
        if (products.length > 0) {
          this.renderResults(products, this.options.messages.recommended);
        } else {
          this.setStatus(this.options.messages.searchHint || '');
          this.resultContainer.innerHTML = '';
        }
      } catch (_) {
        this.setStatus(this.options.messages.searchHint || '');
        this.resultContainer.innerHTML = '';
      }
    }

    async runSearch() {
      const term = (this.searchInput?.value || '').trim();
      if (term.length < 2) {
        if (term.length === 0) this.loadRecommendations();
        else {
          this.setStatus(this.options.messages.searchHint);
          this.resultContainer.innerHTML = '';
        }
        return;
      }
      this.setStatus(this.options.messages.loading);
      try {
        const products = await this.fetchSearchProducts(term, 10);
        this.renderResults(products, this.options.messages.searchResults);
      } catch (_) {
        this.setStatus(this.options.messages.error);
        this.resultContainer.innerHTML = '';
      }
    }

    rootRoute() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    normalizeProduct(product) {
      if (!product) return null;
      const rawId = String(product.id || '');
      const id = rawId.includes('/') ? rawId.split('/').pop() : rawId;
      let image = product.featured_image;
      let imageUrl = '';
      if (typeof image === 'string') imageUrl = image;
      else if (image && typeof image === 'object') imageUrl = image.url || image.src || '';
      return {
        id,
        title: product.title || '',
        handle: product.handle || '',
        url: absoluteUrl(product.url || (product.handle ? `/products/${product.handle}` : '')),
        image: imageUrl
      };
    }

    escapeHtml(value) {
      return String(value || '').replace(/[&<>'"]/g, (char) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
      }[char]));
    }

    renderResults(products, heading) {
      const excluded = this.excludedIds();
      const unique = new Map();
      products.map((p) => this.normalizeProduct(p)).filter(Boolean).forEach((product) => {
        if (!product.id) return;
        if (excluded.has(String(product.id))) return;
        unique.set(product.id, product);
      });
      const rows = [...unique.values()];
      this.setStatus(rows.length ? (heading || this.options.messages.searchResults) : this.options.messages.empty);
      if (rows.length === 0) {
        this.resultContainer.innerHTML = '';
        this.updateCount();
        return;
      }
      this.resultContainer.innerHTML = rows.map((product) => {
        const checked = this.draft.has(product.id);
        const image = product.image
          ? `<img src="${this.escapeHtml(product.image)}" alt="" loading="lazy" width="50" height="50">`
          : '<span class="product-picker__result-image" aria-hidden="true"></span>';
        return `
          <label class="product-picker__result${checked ? ' is-selected' : ''}" data-picker-result-id="${this.escapeHtml(product.id)}">
            ${image}
            <span class="product-picker__result-title">${this.escapeHtml(product.title)}</span>
            <input type="checkbox" value="${this.escapeHtml(product.id)}"${checked ? ' checked' : ''}>
          </label>
        `;
      }).join('');
      rows.forEach((product) => {
        const row = this.resultContainer.querySelector(`[data-picker-result-id="${CSS.escape(product.id)}"]`);
        if (row) row._quoteProduct = product;
      });
      this.updateCount();
    }
  }

  if (typeof window !== 'undefined') {
    window.UncommonProductPicker = ProductPicker;
  }
})();
